import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/context/ToastContext';
import FamilyMemberForm from '../components/FamilyMemberForm';
import ConfirmDialog from '../components/ConfirmDialog';
import GlobalLoader from '@/components/context/GlobalLoader';
import type { FamilyMember, FamilyMemberFormData } from '../types/family';
import { calculateAge, isMinorForHipaa } from '../utils/stateConsentAges';
import { Baby, Heart, UserPlus, Users } from 'lucide-react';
import '@styles/pages/family.css';

export default function FamilyMembers() {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [hipaaStatus, setHipaaStatus] = useState<
    Record<string, { signed: boolean; signedAt: string | null }>
  >({});
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  /* ---------------------------
     LOAD FAMILY MEMBERS + HIPAA STATUS
  ---------------------------- */
  const loadData = async () => {
    if (!user) return;

    const { data: membersData } = await supabase
      .from('family_members')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });

    const familyMembers = membersData ?? [];
    setMembers(familyMembers);

    // Check HIPAA consent status for each family member
    if (familyMembers.length > 0) {
      const memberIds = familyMembers.map((m) => m.id);
      const { data: consents } = await supabase
        .from('hipaa_consents')
        .select('family_member_id, created_at')
        .eq('user_id', user.id)
        .in('family_member_id', memberIds);

      const statusMap: Record<string, { signed: boolean; signedAt: string | null }> = {};
      for (const m of familyMembers) {
        const consent = consents?.find((c) => c.family_member_id === m.id);
        statusMap[m.id] = {
          signed: !!consent,
          signedAt: consent?.created_at ?? null,
        };
      }
      setHipaaStatus(statusMap);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (authLoading || !user) return;
    queueMicrotask(() => loadData());
  }, [authLoading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------------------
     ADD MEMBER
  ---------------------------- */
  const handleAdd = async (data: FamilyMemberFormData) => {
    if (!user) return;
    setFormLoading(true);
    setFormError(null);

    // Check for duplicate (same name + DOB)
    const { data: existing } = await supabase
      .from('family_members')
      .select('id')
      .eq('user_id', user.id)
      .ilike('first_name', data.first_name)
      .ilike('last_name', data.last_name)
      .eq('date_of_birth', data.date_of_birth)
      .maybeSingle();

    if (existing) {
      setFormError('A family member with this name and date of birth already exists.');
      setFormLoading(false);
      return;
    }

    const { error } = await supabase.from('family_members').insert({
      user_id: user.id,
      ...data,
    });

    if (error) {
      setFormError(error.message);
      setFormLoading(false);
      return;
    }

    showToast('Family member added!');
    setShowAddForm(false);
    setFormLoading(false);
    await loadData();
  };

  /* ---------------------------
     EDIT MEMBER
  ---------------------------- */
  const handleEdit = async (data: FamilyMemberFormData) => {
    if (!editingMember) return;
    setFormLoading(true);
    setFormError(null);

    const { error } = await supabase
      .from('family_members')
      .update(data)
      .eq('id', editingMember.id);

    if (error) {
      setFormError(error.message);
      setFormLoading(false);
      return;
    }

    showToast('Family member updated!');
    setEditingMember(null);
    setFormLoading(false);
    await loadData();
  };

  /* ---------------------------
     DELETE MEMBER
  ---------------------------- */
  const handleDelete = async () => {
    if (!deleteConfirmId) return;

    const { error } = await supabase
      .from('family_members')
      .delete()
      .eq('id', deleteConfirmId);

    if (error) {
      showToast('Failed to remove family member', 'error');
    } else {
      showToast('Family member removed');
      await loadData();
    }
    setDeleteConfirmId(null);
  };

  /* ---------------------------
     SIGN HIPAA, routes to signing page
  ---------------------------- */
  const handleSign = (member: FamilyMember) => {
    const isSpouse = member.relationship === 'spouse';
    const isAdultChild =
      member.relationship === 'child' && !isMinorForHipaa(member.date_of_birth);

    if (isSpouse || isAdultChild) {
      // Adults sign for themselves via device handoff
      navigate(`/hipaa/family/${member.id}?handoff=true`);
    } else {
      // Minor children, parent signs
      navigate(`/hipaa/family/${member.id}`);
    }
  };

  /* ---------------------------
     UI
  ---------------------------- */
  if (loading) return <GlobalLoader message="Gathering the case files…" />;

  // Show add form
  if (showAddForm) {
    return (
      <div className="dashboard-container">
        <main className="family-content">
          <h1 className="family-heading">Add Family Member</h1>
          <div className="family-form-section">
            <FamilyMemberForm
              onSubmit={handleAdd}
              onCancel={() => {
                setShowAddForm(false);
                setFormError(null);
              }}
              loading={formLoading}
              error={formError}
            />
          </div>
        </main>
      </div>
    );
  }

  // Show edit form
  if (editingMember) {
    return (
      <div className="dashboard-container">
        <main className="family-content">
          <h1 className="family-heading">Edit Family Member</h1>
          <div className="family-form-section">
            <FamilyMemberForm
              initialData={editingMember}
              onSubmit={handleEdit}
              onCancel={() => {
                setEditingMember(null);
                setFormError(null);
              }}
              loading={formLoading}
              error={formError}
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="family-content">
        <h1 className="family-heading">Family</h1>
        <p className="family-subtitle">
          Add the people whose medical bills you want us to work on. Each person signs their own HIPAA authorization.
        </p>

        {members.length === 0 ? (
          <div className="family-empty">
            <div className="family-empty-icon">
              <Users size={36} />
            </div>
            <p className="family-empty-title">No family members yet</p>
            <p className="family-empty-desc">
              Add a child or spouse to submit bills on their behalf.
            </p>
            <button
              className="family-add-btn"
              onClick={() => setShowAddForm(true)}
            >
              <UserPlus size={16} />
              Add Family Member
            </button>
          </div>
        ) : (
          <>
            <div className="family-list">
              {members.map((member) => {
                const age = calculateAge(member.date_of_birth);
                const status = hipaaStatus[member.id];
                const hasSigned = status?.signed ?? false;
                const signedAt = status?.signedAt;
                const Icon = member.relationship === 'child' ? Baby : Heart;

                const signedDateStr = signedAt
                  ? new Date(signedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : null;

                return (
                  <div key={member.id} className="family-card">
                    <div className="family-card-icon">
                      <Icon size={20} />
                    </div>

                    <div className="family-card-info">
                      <div className="family-card-name">
                        {member.first_name} {member.last_name}
                      </div>
                      <div className="family-card-meta">
                        <span className={`family-badge ${member.relationship}`}>
                          {member.relationship === 'child' ? 'Child' : 'Spouse'}
                        </span>
                        <span className="family-card-age">
                          Age {age}
                        </span>
                        {hasSigned ? (
                          <span className="family-badge signed">HIPAA Signed</span>
                        ) : (
                          <span className="family-badge unsigned">Needs HIPAA</span>
                        )}
                      </div>
                      {signedDateStr && (
                        <div className="family-card-signed-date">
                          Signed {signedDateStr}
                        </div>
                      )}
                    </div>

                    <div className="family-card-actions">
                      {!hasSigned && (
                        <>
                          <button
                            className="sign-btn"
                            onClick={() => handleSign(member)}
                          >
                            Sign HIPAA
                          </button>
                          <button onClick={() => setEditingMember(member)}>
                            Edit
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => setDeleteConfirmId(member.id)}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              className="family-add-btn"
              onClick={() => setShowAddForm(true)}
            >
              <UserPlus size={16} />
              Add Family Member
            </button>
          </>
        )}
      </main>

      <ConfirmDialog
        open={!!deleteConfirmId}
        title="Remove family member?"
        message="This will also remove any associated HIPAA authorizations. Bills already submitted will not be affected."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmId(null)}
        variant="danger"
      />
    </div>
  );
}
